import React, { useState, useRef } from 'react';
import { Clock, Percent, ArrowRight, MessageCircle, Play, Pause, Calendar, Sun } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';

const IMAGES = [
  'https://images.unsplash.com/photo-1749132403012-24034a90a7ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHx0aGFpJTIwbWFzc2FnZSUyMHRoZXJhcGlzdCUyMG9pbCUyMHRyZWF0bWVudCUyMGVuY2xvc2VkJTIwcm9vbSUyMHdhcm0lMjBkaW18ZW58MHx8fHwxNzc2MTIwODQwfDA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHx0aGFpJTIwbWFzc2FnZSUyMHNwYSUyMGluZG9vciUyMGx1eHVyeSUyMGVuY2xvc2VkJTIwd2FybSUyMGxpZ2h0aW5nJTIwdHJlYXRtZW50fGVufDB8fHx8MTc3NjEyMDgxOXww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjB3b21hbiUyMHJlbGF4aW5nJTIwYmFjayUyMG1hc3NhZ2UlMjBzcGElMjBoYW5kcyUyMG9pbHxlbnwwfHx8fDE3NzYxMjAxMTd8MA&ixlib=rb-4.1.0&q=85',
];

const VIDEO_URL = '/lunchtime-massage-v5.mp4';
const VIDEO_POSTER = 'https://images.unsplash.com/photo-1639758597299-81abeebc5f9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjB3b21hbiUyMHNwYSUyMG1hc3NhZ2UlMjByZWxheGluZyUyMGZhY2UlMjBzZXJlbmV8ZW58MHx8fHwxNzc2MTIxNDc4fDA&ixlib=rb-4.1.0&q=85';

const SHARE_MSG = `Lunchtime Deal at Dublin Holistic Massage!\n\n10% OFF any massage treatment booked Mon-Fri between 12pm-3pm. All therapists, both locations!\n\nBook: www.dublinholisticmassages.com\nWhatsApp: +353 85 774 7474\n\nTreat yourself this lunchtime!`;

const SHARE_VIDEO_MSG = `Watch this relaxing massage and imagine yourself here during your lunch break!\n\nGet 10% OFF lunchtime massages (Mon-Fri, 12-3pm) at Dublin Holistic Massage.\n\nBook: www.dublinholisticmassages.com`;

const LunchtimeDeal = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <>
      <section
        className="py-20 relative overflow-hidden"
        data-testid="lunchtime-deal"
        style={{ background: 'linear-gradient(135deg, #0F2922 0%, #16382E 40%, #1C473A 70%, #0F2922 100%)' }}
      >
        {/* Decorative sun burst */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-6 border border-amber-500/30">
              <Sun size={14} />
              Lunchtime Special
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight">
              Lunchtime Deal
              <span className="block text-amber-400 italic mt-2 text-2xl sm:text-3xl lg:text-4xl">
                10% Off Every Treatment
              </span>
            </h2>

            <p className="text-white/70 font-light max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
              Take a break from your busy day and treat yourself to a rejuvenating massage during lunch.
              Available <strong className="text-white">Monday to Friday, 12pm - 3pm</strong> with all therapists at both locations.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-start">

            {/* Left — Video + Images */}
            <div className="space-y-4">
              {/* Video Player */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-amber-500/30 group cursor-pointer" onClick={toggleVideo}>
                <video
                  ref={videoRef}
                  poster={VIDEO_POSTER}
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-[300px] sm:h-[380px] object-cover"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  data-testid="lunchtime-video"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
                <div className={`absolute inset-0 bg-black/30 flex items-center justify-center transition-opacity ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 hover:scale-110 transition-transform">
                    {isPlaying ? <Pause size={28} className="text-white" /> : <Play size={28} className="text-white ml-1" />}
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <p className="text-white text-sm font-medium">Relaxation awaits during your lunch break</p>
                </div>
              </div>

              {/* Image Gallery */}
              <div className="grid grid-cols-3 gap-3">
                {IMAGES.map((img, i) => (
                  <div key={img} className="rounded-xl overflow-hidden border border-white/10 hover:border-amber-500/40 transition-colors">
                    <img src={img} alt={`Spa massage treatment ${i + 1}`} className="w-full h-24 sm:h-28 object-cover" loading="lazy" />
                  </div>
                ))}
              </div>

              {/* Share Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
                  className="flex-1 bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-4 text-xs font-medium"
                  data-testid="lunchtime-whatsapp-share"
                >
                  <MessageCircle size={16} className="mr-2" />
                  Share Deal
                </Button>
                <Button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_VIDEO_MSG)}`, '_blank')}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-4 text-xs font-medium border border-white/20"
                  data-testid="lunchtime-whatsapp-video"
                >
                  <Play size={16} className="mr-2" />
                  Share Video
                </Button>
              </div>
            </div>

            {/* Right — Deal Details */}
            <div className="space-y-5">

              {/* Big Savings Card */}
              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/5 rounded-3xl p-6 sm:p-8 border border-amber-500/40 text-center">
                <div className="text-amber-400 text-7xl font-display font-bold mb-2">10%</div>
                <p className="text-white text-xl font-semibold mb-1">OFF Any Treatment</p>
                <p className="text-white/60 text-sm mb-4">Monday - Friday, 12pm - 3pm</p>

                <div className="bg-white/10 rounded-xl p-4 mb-5 text-left">
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock size={14} className="text-amber-400" />
                      </div>
                      <span className="text-white/90 text-sm">Valid <strong className="text-white">12pm - 3pm</strong> Mon to Fri</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Calendar size={14} className="text-amber-400" />
                      </div>
                      <span className="text-white/90 text-sm">Works with <strong className="text-white">all therapists</strong></span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Percent size={14} className="text-amber-400" />
                      </div>
                      <span className="text-white/90 text-sm">Applied <strong className="text-amber-400">automatically</strong> at checkout — no code needed</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setBookingOpen(true)}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 rounded-full py-6 text-sm uppercase tracking-wider font-bold group"
                  data-testid="lunchtime-book-btn"
                >
                  Book Your Lunchtime Massage
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                <Button
                  onClick={() => window.open('https://wa.me/353857747474?text=Hi! I\'d like to book a lunchtime massage deal (10% off, Mon-Fri 12-3pm)', '_blank')}
                  className="w-full mt-3 bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-5 text-sm font-medium"
                  data-testid="lunchtime-whatsapp-book"
                >
                  <MessageCircle size={18} className="mr-2" />
                  WhatsApp +353 85 774 7474
                </Button>
              </div>

              {/* Deal Note */}
              <div className="bg-gradient-to-r from-emerald-900/40 to-emerald-800/20 rounded-2xl p-5 border border-emerald-500/30 text-center">
                <p className="text-emerald-400 font-display text-lg mb-1">Automatic Savings</p>
                <p className="text-white/80 text-sm">
                  Book any weekday between <strong className="text-white">12pm and 3pm</strong> and your <strong className="text-emerald-400">10% discount</strong> is applied automatically. Cannot be combined with other offers.
                </p>
              </div>

              {/* Locations */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-3 font-semibold">Available at both locations</p>
                <div className="flex gap-3">
                  <span className="bg-white/10 text-white/80 px-4 py-2 rounded-full text-sm border border-white/15 flex-1 text-center">City Centre</span>
                  <span className="bg-white/10 text-white/80 px-4 py-2 rounded-full text-sm border border-white/15 flex-1 text-center">Clarehall</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
};

export default LunchtimeDeal;
